import React, { useState, useEffect } from "react";
import { GetWaterProfileOrders } from "../../services/ClientWaterProfileService";
import { NotificationManager } from "react-notifications";
import CommonLoader from "../common/CommonLoader";
import OrderManager from "./OrderManager"; // Import the new component

export default function ComparisonArea({
  selectedOrders = [],
  selectedWaterProfile = null,
  onRemoveOrder,
  onRemoveWaterProfile,
  onSelectOrders,
  onSelectWaterProfile,
  accountNumber, // Add accountNumber prop
  className = "",
}) {
  const [loading, setLoading] = useState(false);
  const [profileOrdersData, setProfileOrdersData] = useState([]);
  const [internalSelectedOrders, setInternalSelectedOrders] =
    useState(selectedOrders);

  const hasSelections =
    internalSelectedOrders.length > 0 || selectedWaterProfile;
  const hasData = profileOrdersData.length > 0 && selectedWaterProfile;

  // Chemical properties mapping
  const chemicalProperties = {
    calcium: { label: "Calcium", unit: "mg/L" },
    totalAlkalinity: { label: "Total Alkalinity", unit: "mg/L" },
    bicarbonate: { label: "Bicarbonate", unit: "mg/L" },
    sulfate: { label: "Sulfate", unit: "mg/L" },
    magnesium: { label: "Magnesium", unit: "mg/L" },
    totalHardness: { label: "Total Hardness", unit: "mg/L" },
    iron: { label: "Iron", unit: "mg/L" },
    pH: { label: "pH", unit: "" },
    totalChlorine: { label: "Total Chlorine", unit: "mg/L" },
    residualAlkalinity: { label: "Residual Alkalinity", unit: "mg/L" },
    chloride: { label: "Chloride", unit: "mg/L" },
    sulfateToChlorideRatio: { label: "Sulfate/Chloride Ratio", unit: "" },
    sodium: { label: "Sodium", unit: "mg/L" },
    zinc: { label: "Zinc", unit: "mg/L" },
    potassium: { label: "Potassium", unit: "mg/L" },
    copper: { label: "Copper", unit: "mg/L" },
    tds: { label: "TDS", unit: "mg/L" },
  };

  // Sync internal selected orders with props when they change
  useEffect(() => {
    setInternalSelectedOrders(selectedOrders);
  }, [selectedOrders]);

  // Fetch profile orders whenever internal selected orders change
  useEffect(() => {
    fetchProfileOrders();
  }, [internalSelectedOrders]);

  const fetchProfileOrders = async () => {
    if (!internalSelectedOrders || internalSelectedOrders.length === 0) {
      setProfileOrdersData([]);
      return;
    }

    try {
      setLoading(true);
      const orderNumbersCsv = internalSelectedOrders.join(",");
      const response = await GetWaterProfileOrders(orderNumbersCsv);
      setProfileOrdersData(response || []);
      console.log("profile Orders Data response : ", response);
    } catch (error) {
      console.error("Error fetching profile Orders Data:", error);
      NotificationManager.error("Error fetching profile orders Data", "Error");
    } finally {
      setLoading(false);
    }
  };

  // Handle order changes from OrderManager
  const handleOrdersChange = (newOrders) => {
    // Update internal state
    setInternalSelectedOrders(newOrders);

    // Call the parent's onSelectOrders if provided to keep parent state in sync
    if (onSelectOrders) {
      onSelectOrders(newOrders);
    }

    // fetchProfileOrders will be called automatically via useEffect
  };

  // Get chemicals that have data in either profile or orders
  const getAvailableChemicals = () => {
    const chemicals = [];

    Object.keys(chemicalProperties).forEach((chemicalKey) => {
      const profileValue = selectedWaterProfile?.[chemicalKey];
      const hasOrderData = profileOrdersData.some(
        (order) =>
          order[chemicalKey] !== null && order[chemicalKey] !== undefined
      );

      if (
        (profileValue !== null && profileValue !== undefined) ||
        hasOrderData
      ) {
        chemicals.push({
          key: chemicalKey,
          ...chemicalProperties[chemicalKey],
        });
      }
    });

    return chemicals;
  };

  const getVarianceColor = (profileValue, orderValue) => {
    if (!profileValue || !orderValue) return "";

    const variance = Math.abs(profileValue - orderValue);
    const percentVariance = (variance / profileValue) * 100;

    if (percentVariance > 20)
      return "has-background-danger-light has-text-danger-dark";
    if (percentVariance > 10)
      return "has-background-warning-light has-text-warning-dark";
    return "has-background-success-light has-text-success-dark";
  };

  const getVariancePercentage = (profileValue, orderValue) => {
    if (!profileValue || !orderValue) return null;
    const variance = Math.abs(profileValue - orderValue);
    return ((variance / profileValue) * 100).toFixed(1);
  };

  const formatValue = (value) => {
    if (value === null || value === undefined) return "-";
    return typeof value === "number" ? value.toFixed(2) : value;
  };

  const renderSelectionArea = () => (
    <div className="box mb-5">
      {!hasSelections ? (
        <div className="has-text-centered py-6">
          <div className="icon-text mb-4">
            <span className="icon is-large has-text-grey-light">
              <i className="fas fa-clipboard-list fa-2x"></i>
            </span>
          </div>
          <p className="has-text-grey">No items selected for comparison yet.</p>
          <p className="has-text-grey is-size-7">
            Select orders and water profiles to start comparing.
          </p>
        </div>
      ) : (
        <div className="content">
          {/* OrderManager component */}
          <OrderManager
            selectedOrders={internalSelectedOrders}
            onOrdersChange={handleOrdersChange}
            accountNumber={accountNumber}
            maxSelection={10}
          />

          {selectedWaterProfile && (
            <div className="mt-4">
              <div className="level is-mobile mb-3">
                <div className="level-left">
                  <div className="level-item">
                    <h6 className="title is-6 has-text-primary">
                      <span className="icon-text">
                        <span className="icon">
                          <i className="fas fa-flask"></i>
                        </span>
                        <span>Selected Water Profile</span>
                      </span>
                    </h6>
                  </div>
                </div>
              </div>

              <div className="box" style={{ padding: "1rem" }}>
                <div className="field is-grouped is-grouped-multiline">
                  <div className="control">
                    <div className="tags has-addons">
                      <span className="tag is-dark">ID</span>
                      <span className="tag is-info is-light">
                        {selectedWaterProfile.id || "N/A"}
                      </span>
                    </div>
                  </div>
                  <div className="control">
                    <div className="tags has-addons">
                      <span className="tag is-dark">Type</span>
                      <span className="tag is-info is-light">
                        {selectedWaterProfile.type || "Unknown Type"}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );

  const renderComparisonTable = () => {
    if (loading) {
      return <CommonLoader loading={loading} />;
    }

    if (!hasData) {
      return (
        <div className="box has-text-centered py-6">
          <div className="icon-text mb-4">
            <span className="icon is-large has-text-grey-light">
              <i className="fas fa-chart-line fa-2x"></i>
            </span>
          </div>
          <p className="has-text-grey">No comparison data available.</p>
          <p className="has-text-grey is-size-7">
            Select both orders and a water profile to view comparison results.
          </p>
        </div>
      );
    }

    const availableChemicals = getAvailableChemicals();

    if (availableChemicals.length === 0) {
      return (
        <div className="box has-text-centered py-6">
          <div className="icon-text mb-4">
            <span className="icon is-large has-text-grey-light">
              <i className="fas fa-flask fa-2x"></i>
            </span>
          </div>
          <p className="has-text-grey">
            No chemical data available for comparison.
          </p>
        </div>
      );
    }

    // Create a map for quick order lookup
    const orderDataMap = {};
    profileOrdersData.forEach((order) => {
      orderDataMap[order.orderNumber] = order;
    });

    return (
      <div className="box">
        <div className="level is-mobile mb-4">
          <div className="level-left">
            <div className="level-item">
              <h5 className="title is-5 has-text-primary">
                <span className="icon-text">
                  <span className="icon">
                    <i className="fas fa-chart-line"></i>
                  </span>
                  <span>Comparison Results</span>
                </span>
              </h5>
            </div>
          </div>
          <div className="level-right">
            <div className="level-item">
              <span className="tag is-info">
                {availableChemicals.length} Chemical
                {availableChemicals.length !== 1 ? "s" : ""} Available
              </span>
            </div>
          </div>
        </div>

          <div className="level is-mobile">
            <div className="level-left">
              <div className="level-item">{/* <strong>Legend:</strong> */}</div>
            </div>
            <div className="level-right">
              <div className="level-item">
                <div className="tags">
                  <span className="tag is-success">
                    <span>Good Match (&lt;10%)</span>
                  </span>
                  <span className="tag is-warning">
                    <span>Moderate (10-20%)</span>
                  </span>
                  <span className="tag is-danger">
                    <span>High Variance (&gt;20%)</span>
                  </span>
                </div>
              </div>
            </div>
          </div>

        <div className="table-container-wrapper">
          <table className="table is-bordered is-striped is-narrow is-hoverable comparison-table">
            <thead>
              <tr>
                <th className="chemical-column">Chemical</th>
                <th className="profile-column">Profile Value</th>
                {internalSelectedOrders.map((orderNumber) => (
                  <th key={orderNumber} className="order-column">
                    <div className="order-header">
                      <div className="has-text-weight-bold">
                        Order {orderNumber}
                      </div>
                      <div className="has-text-grey is-size-7">
                        Value | Variance
                      </div>
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {availableChemicals.map((chemical) => {
                const profileValue = selectedWaterProfile[chemical.key];

                return (
                  <tr key={chemical.key}>
                    <td className="chemical-column">
                      <div className="chemical-info">
                        <div className="has-text-weight-semibold">
                          {chemical.label}
                        </div>
                        {chemical.unit && (
                          <div className="has-text-grey is-size-7">
                            ({chemical.unit})
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="profile-column has-text-centered">
                      <span className="tag is-primary">
                        {formatValue(profileValue)}
                      </span>
                    </td>
                    {internalSelectedOrders.map((orderNumber) => {
                      const orderData = orderDataMap[orderNumber];
                      const orderValue = orderData
                        ? orderData[chemical.key]
                        : null;
                      const variancePercent = getVariancePercentage(
                        profileValue,
                        orderValue
                      );
                      const colorClass = getVarianceColor(
                        profileValue,
                        orderValue
                      );

                      return (
                        <td
                          key={orderNumber}
                          className="order-column has-text-centered"
                        >
                          {orderValue !== null && orderValue !== undefined ? (
                            <div className="order-value-container">
                              <div className={`value-cell ${colorClass}`}>
                                <div className="order-value">
                                  {formatValue(orderValue)}
                                </div>
                                {variancePercent && (
                                  <div className="variance-value is-size-7">
                                    ±{variancePercent}%
                                  </div>
                                )}
                              </div>
                            </div>
                          ) : (
                            <span className="tag is-light">-</span>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  return (
    <div className={`${className}`}>
      <style jsx>{`
        .table-container-wrapper {
          overflow: auto;
          max-height: 600px;
          border: 1px solid #dbdbdb;
          border-radius: 4px;
        }

        .comparison-table {
          margin: 0;
          min-width: 100%;
        }

        .comparison-table thead th {
          background-color: #f5f5f5;
          position: sticky;
          top: 0;
          z-index: 10;
          white-space: nowrap;
        }

        .chemical-column {
          min-width: 140px;
          max-width: 140px;
          position: sticky;
          left: 0;
          background-color: #fafafa;
          z-index: 5;
        }

        .profile-column {
          min-width: 80px;
          position: sticky;
          left: 140px;
          background-color: #fafafa;
          z-index: 5;
        }

        .order-column {
          min-width: 120px;
          width: 120px;
        }

        .chemical-info {
          padding: 0.5rem 0;
        }

        .order-header {
          text-align: center;
          padding: 0.25rem;
        }

        .order-value-container {
          padding: 0.25rem;
        }

        .value-cell {
          border-radius: 4px;
          padding: 0.5rem;
          margin: 0.1rem;
          border: 1px solid transparent;
        }

        .order-value {
          font-weight: 600;
          font-size: 0.9rem;
        }

        .variance-value {
          margin-top: 0.2rem;
          opacity: 0.8;
        }

        .comparison-table tbody tr:hover .chemical-column,
        .comparison-table tbody tr:hover .profile-column {
          background-color: #f0f0f0;
        }

        .has-background-success-light {
          background-color: #effaf3 !important;
        }

        .has-background-warning-light {
          background-color: #fffbeb !important;
        }

        .has-background-danger-light {
          background-color: #feecf0 !important;
        }

        .has-text-success-dark {
          color: #257942 !important;
        }

        .has-text-warning-dark {
          color: #947600 !important;
        }

        .has-text-danger-dark {
          color: #cc0f35 !important;
        }

        @media screen and (max-width: 768px) {
          .table-container-wrapper {
            max-height: 400px;
          }

          .chemical-column {
            min-width: 120px;
            max-width: 120px;
          }

          .profile-column {
            left: 120px;
            min-width: 70px;
          }

          .order-column {
            min-width: 100px;
            width: 100px;
          }
        }
      `}</style>

      {renderSelectionArea()}
      {renderComparisonTable()}
    </div>
  );
}
